/**
 * Deep-link to the console.automately.net vendor's agent chat screen.
 *
 * Context (2026-10-06): Gamp Finserv (tenant 15) hosts its WhatsApp Business
 * number on the console.automately.net BSP. Agents work two surfaces at
 * once — the CRM for lead data + the vendor UI for the actual chat. The
 * cross-link saves them from searching the chat list by phone every time.
 *
 * URL format discovered by inspecting the vendor UI:
 *   https://agent.automately.net/home/chat/<base64(phoneWithoutPlus)>
 *
 * Example: phone 919711086568 -> base64 "OTE5NzExMDg2NTY4"
 *          final URL: https://agent.automately.net/home/chat/OTE5NzExMDg2NTY4
 *
 * Scope: only Gamp (tenant 15) + leads whose source is WhatsApp. Other
 * tenants either don't use this vendor or use Meta Cloud API directly.
 * When another tenant onboards the same BSP later, promote
 * GAMP_WHATSAPP_VENDOR_TENANTS into a backend-driven config column on
 * tenants (whatsapp_vendor_base_url) rather than growing this constant.
 */

const WHATSAPP_VENDOR_BASE_URL = 'https://agent.automately.net/home/chat/';

const GAMP_WHATSAPP_VENDOR_TENANTS: ReadonlySet<number> = new Set([15]);

type Leadish = {
  tenant_id?: number | null;
  source?: string | null;
  mobile?: string | null;
  phone?: string | null;
  whatsapp_number?: string | null;
};

/**
 * Returns the vendor's deep-link URL for a lead, or null if the lead is
 * not eligible for the cross-link (wrong tenant, wrong source, no phone).
 */
export function whatsappVendorChatUrl(lead: Leadish): string | null {
  if (!lead.tenant_id || !GAMP_WHATSAPP_VENDOR_TENANTS.has(lead.tenant_id)) {
    return null;
  }
  if ((lead.source ?? '').toLowerCase() !== 'whatsapp') {
    return null;
  }

  // Prefer whatsapp_number if the lead has one explicitly, fall back to
  // mobile/phone. The vendor keys chats by the number the user wrote to
  // our business line from, which is the same digits that landed in
  // mobile for webhook-ingested leads.
  const raw = lead.whatsapp_number || lead.mobile || lead.phone || '';
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length < 10) {
    return null;
  }

  // The vendor's path segment is base64 of the plain digit string.
  // btoa handles ASCII fine; phone numbers are ASCII.
  const encoded = typeof window !== 'undefined' && window.btoa
    ? window.btoa(digits)
    : Buffer.from(digits, 'binary').toString('base64');

  return WHATSAPP_VENDOR_BASE_URL + encoded;
}
